import { commaValueFormatter, Select } from '@eleveo/components-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { useIntegrations } from '../../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../../constants/dataTestIds';
import { USER_SYNCHRONIZATION_FIELDS } from '../../../../scene/integrations/types';
import { recordBy } from '../../../../utils/arrayUtils';
import { createRoleOption } from '../../../../utils/roleUtils';
import { useIntegrationRoles } from '../../roles/IntegrationRolesProvider';

export const FieldGroupSupervisors: React.FC = () => {
  const { t } = useTranslation();
  const { isInReadOnlyMode, integrationType } = useIntegrations();
  const { isIntegrationDataFilled, integrationRoles } = useIntegrationRoles();
  const [isOpened, setIsOpened] = useState(false);

  const {
    input: { value: groupSupervisors, onBlur, onFocus, onChange },
  } = useField<string[]>(USER_SYNCHRONIZATION_FIELDS.groupSupervisors);

  const [selectedRoles, setSelectedRoles] = useState<Set<string | number>>(new Set(groupSupervisors));

  const groupSupervisorOptions = useMemo(() => {
    if (!groupSupervisors.length || !integrationRoles?.length) {
      return integrationRoles;
    }

    const integrationRolesById = recordBy(integrationRoles, 'id');

    // show only roles that were previously assigned and removed
    const missingRoleIds = groupSupervisors.filter((roleId) => !integrationRolesById[roleId]);

    if (!missingRoleIds.length) {
      return integrationRoles;
    }

    return [
      { name: t('myConfigurations.roleMapping.roles.deleted'), children: missingRoleIds.map(createRoleOption) },
      {
        name: t('myConfigurations.roleMapping.roles.providerRoles', {
          providerName: t(`providers.${integrationType}`),
        }),
        children: integrationRoles,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSupervisors, integrationRoles]);

  const submitGroupSupervisors = useCallback(
    (submittedRoles?: Set<string | number>) => {
      const newRoles = submittedRoles ?? selectedRoles;
      if (!newRoles) {
        // nothing to update
        return;
      }

      onChange(Array.from(newRoles));
      setSelectedRoles(newRoles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRoles]
  );

  if (!isIntegrationDataFilled || !integrationRoles?.length || !groupSupervisorOptions) {
    return null;
  }

  return (
    <Select
      isDisabled={isInReadOnlyMode}
      className="w-full mbe-20"
      isClearable
      isSearchable
      onBlur={onBlur}
      onFocus={onFocus}
      selectedKeys={selectedRoles}
      label={t('myConfigurations.roleMapping.groupSupervisor')}
      options={groupSupervisorOptions}
      onSelectionChange={(values) => {
        if (isOpened) {
          setSelectedRoles(values);
          return;
        }

        submitGroupSupervisors(values);
      }}
      onOpenChange={(isOpen) => {
        setIsOpened(isOpen);
        if (isOpen) {
          return;
        }

        submitGroupSupervisors();
      }}
      selectionMode="multiple"
      validationState={selectedRoles.size ? 'valid' : undefined}
      valueFormatter={commaValueFormatter}
      testId={TEST_IDS.selectGroupSupervisors}
    />
  );
};
